import { z } from 'zod'
import { leagueIdSchema } from '../league-catalog'
import { goalSnapshotSchema } from './goal-schema'

export const chatHistoryPageSize = 50

export const interruptedChatRetryMessage =
  'Continue my interrupted request from its saved progress. Inspect existing action receipts and operations before continuing; do not repeat completed or uncertain actions.'

/**
 * The workspace sends this prompt, not the user, when it resumes a waiting message (ide-distributions CONTRACT.md,
 * "Message admissions"). It has `client_context: null`; a user's message with the same words carries its own context.
 */
export const workspaceContinuationPrefix = 'Your previous run stopped because '

/** Display archived string messages without changing their stored content. New sends carry context separately. */
export function historicalMessageText(content: string) {
  const index = content.lastIndexOf('\n\nContext v1:\n')
  return index < 0 ? content : content.slice(0, index)
}

export const conversationIdSchema = z
  .string()
  .min(1)
  .max(200)
  .regex(/^[A-Za-z0-9_-]+$/)
export const chatInputSchema = z.object({
  message: z.string().trim().min(1).max(50000),
  context: z.record(z.string(), z.unknown()),
})
export const chatCommandSchema = z.discriminatedUnion('action', [
  z.object({ action: z.literal('create'), conversationId: conversationIdSchema, leagueId: leagueIdSchema }),
  z.object({
    action: z.literal('send'),
    conversationId: conversationIdSchema,
    requestId: z.uuid(),
    ...chatInputSchema.shape,
  }),
  z.object({ action: z.literal('reconcile'), conversationId: conversationIdSchema, requestId: z.uuid() }),
  z.object({ action: z.literal('stop'), conversationId: conversationIdSchema, requestId: z.uuid() }),
  z.object({
    action: z.literal('approval'),
    conversationId: conversationIdSchema,
    requestId: z.uuid(),
    approvalId: z
      .string()
      .min(1)
      .max(256)
      .regex(/^[A-Za-z0-9_-]+$/),
    choice: z.enum(['once', 'deny']),
  }),
])
export const nativeApprovalSchema = z.object({
  request_id: z.string().min(1).max(256),
  command: z.string().optional(),
  description: z.string().optional(),
  choices: z.array(z.string()),
})
export const nativeRunSchema = z
  .object({
    run_id: z.string(),
    status: z.enum([
      'queued',
      'running',
      'waiting_for_approval',
      'stopping',
      'completed',
      'cancelled',
      'failed',
      'interrupted',
    ]),
    output: z.string().optional(),
    error: z.unknown().optional(),
    error_surface: z.object({ code: z.string(), layer: z.string(), retryable: z.boolean() }).nullish(),
    session_id: z.string().optional(),
    usage: z.record(z.string(), z.number()).optional(),
    pending_steer: z.unknown().optional(),
    approval: nativeApprovalSchema.optional(),
  })
  .passthrough()
export type NativeRun = z.infer<typeof nativeRunSchema>
export const isTerminalRun = (run: Pick<NativeRun, 'status'>) =>
  ['completed', 'cancelled', 'failed', 'interrupted'].includes(run.status)
export const nativeMessageSchema = z.object({
  id: z.union([z.number(), z.string()]),
  role: z.string(),
  content: z.unknown(),
  client_context: z.record(z.string(), z.json()).nullish(),
  display_kind: z.string().nullish(),
  tool_call_id: z.string().nullish(),
  tool_name: z.string().nullish(),
  tool_calls: z
    .array(z.object({ id: z.string(), function: z.object({ name: z.string(), arguments: z.string() }) }))
    .nullish(),
  reasoning: z.string().nullish(),
  reasoning_content: z.string().nullish(),
})
export const chatRequestSchema = z.object({
  // Optional protocol extension while local UI uses the deployed API. Database rows are migrated.
  source: z.enum(['user', 'continuation']).optional(),
  conversation_id: conversationIdSchema,
  request_id: z.uuid(),
  request: chatInputSchema,
  payload: z.record(z.string(), z.unknown()),
  run_id: z.string().nullable(),
  terminal: z
    .union([
      nativeRunSchema,
      nativeRunSchema.extend({ run_id: z.null(), status: z.literal('interrupted'), error: z.string() }),
    ])
    .nullable(),
  created_at: z.string(),
})
export type ChatRequest = z.infer<typeof chatRequestSchema>
export const archiveSchema = z.object({
  source: z.literal('eve'),
  exportedAt: z.iso.datetime(),
  sha256: z.string().regex(/^[a-f0-9]{64}$/),
  events: z.array(z.unknown()),
  messages: z.array(z.object({ role: z.enum(['user', 'assistant']), text: z.string() })),
})
export const archiveSummarySchema = archiveSchema.omit({ events: true })
export const nativeConversationSchema = z.object({
  eve_session_id: conversationIdSchema,
  student_id: z.string(),
  league_id: leagueIdSchema,
  hermes_session_id: z.string(),
  transport: z.enum(['eve', 'migrating', 'hermes']),
  archive: archiveSchema.nullable(),
  history_after: z.number().int().nonnegative(),
  continuation_stopped: z.boolean().optional(),
})
export const sessionCreationSchema = z.discriminatedUnion('status', [
  z.object({ status: z.literal('starting') }),
  z.object({ status: z.literal('ready') }),
  z.object({ status: z.literal('failed'), error: z.string() }),
])
export type ChatSendResult =
  | { requestId: string; run: NonNullable<ChatRequest['terminal']> }
  | { requestId: string; run: null; session?: z.infer<typeof sessionCreationSchema> }
export const chatContinuationSchema = z.object({
  id: z.uuid(),
  state: z.enum(['waiting', 'ready', 'admitted', 'blocked']),
  next_check_at: z.string(),
  reason: z.string().nullable(),
  result: z.unknown().optional(),
})
export const chatSnapshotSchema = z.object({
  session: z.union([
    sessionCreationSchema,
    z.object({ status: z.literal('sleeping') }),
    z.object({ status: z.literal('missing'), error: z.string() }),
  ]),
  conversationId: conversationIdSchema,
  messages: z.array(nativeMessageSchema),
  archive: archiveSummarySchema.nullable(),
  observationError: z.string().nullable(),
  requests: z.array(chatRequestSchema.omit({ payload: true })),
  calls: z.array(
    z.object({ request_id: z.string(), tool: z.string(), input: z.unknown(), result: z.unknown(), status: z.string() })
  ),
  active: nativeRunSchema.nullable(),
  activeHistoryAfter: z.number().int().nonnegative().optional(),
  admissionUncertain: z.boolean(),
  continuationStopped: z.boolean().optional(),
  hasOlderMessages: z.boolean(),
  continuations: z.array(chatContinuationSchema).optional(),
  goal: goalSnapshotSchema.nullable().optional(),
})
export type ChatSnapshot = z.infer<typeof chatSnapshotSchema>
